import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { FullInvoice } from 'entities';

import { Column, Row, Alert, Tag, IconButton } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { TruncateText } from 'components/TruncateText';

import {
  ReceiptLongIcon,
  WalletIcon,
  LocationPinIcon,
  UserIcon,
  DollarBillIcon,
  CirclePercentageIcon,
  UserSquareIcon,
  LandmarkIcon,
  MailIcon,
  CalendarIcon,
  LocationMapIcon,
  DocumentTextIcon,
  ChatIcon,
  CalendarDaysIcon,
  ExternalLinkIcon,
  FolderIcon,
  PackageCheckIcon,
} from 'components/Icons';

import { formatCurrency } from 'utils/text';
import { formatDate } from 'utils/date';

import { InvoiceTabProps } from './types';

const invoiceProps: Array<{
  getName: (t: TFunction) => string;
  extractor: (invoice: FullInvoice, t: TFunction) => string | ReactNode;
  Icon: typeof LocationPinIcon;
  shouldRender?: (invoice: FullInvoice) => boolean;
}> = [
  {
    getName: (t: TFunction) => t('country'),
    extractor: ({ country }) => country,
    Icon: LocationPinIcon,
  },
  {
    getName: (t: TFunction) => t('value'),
    Icon: DollarBillIcon,
    extractor: ({ currency, value }) => {
      return (
        <Row gap="1.5">
          <span className="text-success-light">{currency}</span>

          <span>{formatCurrency(value)}</span>
        </Row>
      );
    },
  },

  {
    getName: (t: TFunction) => t('additional-percentage'),
    extractor: ({ additionalPercentage }) =>
      `${additionalPercentage?.name} - ${additionalPercentage?.value}%`,
    Icon: CirclePercentageIcon,
    shouldRender: (invoice) => !!invoice?.additionalPercentage,
  },
  {
    getName: (t: TFunction) => t('client'),
    Icon: UserSquareIcon,
    extractor: ({ clientName }) => clientName,
  },
  {
    getName: (t: TFunction) => t('company-name'),
    Icon: LandmarkIcon,
    extractor: ({ companyName }) => companyName,
  },
  {
    getName: () => 'Emails',
    Icon: MailIcon,
    extractor: ({ emails }) => `${emails.join(', ')}`,
  },
  {
    getName: (t: TFunction) => t('client-po'),
    Icon: UserSquareIcon,
    extractor: ({ poNumber }) => poNumber,
  },
  {
    getName: (t: TFunction) => t('client-pm'),
    Icon: UserIcon,
    extractor: ({ clientPM }) => clientPM,
    shouldRender: ({ clientPM }) => !!clientPM,
  },
  {
    getName: (t: TFunction) => t('tax-id'),
    Icon: UserSquareIcon,
    extractor: ({ taxId }) => taxId,
  },
  {
    getName: (t: TFunction) => t('date-reference'),
    Icon: CalendarIcon,
    extractor: ({ dateRequest }, t) =>
      dateRequest.value
        ? `${t(dateRequest.type)} - ${
            dateRequest.type === 'EXACT' ? formatDate(dateRequest.value) : dateRequest.value
          }`
        : t('not-specified'),
  },
  {
    getName: (t: TFunction) => t('address'),
    extractor: ({ address }) => address,
    Icon: LocationMapIcon,
  },
  {
    getName: (t: TFunction) => t('description'),
    extractor: ({ description }) => description,
    Icon: DocumentTextIcon,
  },
  {
    getName: (t: TFunction) => t('comments'),
    extractor: ({ comment }) => comment || 'N/A',
    Icon: ChatIcon,
  },
  {
    getName: (t: TFunction) => t('invoiced-at'),
    extractor: ({ invoicedAt }) => invoicedAt,
    Icon: CalendarDaysIcon,
    shouldRender: ({ invoicedAt }) => !!invoicedAt,
  },
  {
    getName: (t: TFunction) => `${t('invoice')} #`,
    extractor: ({ accountantId }) => accountantId,
    Icon: ReceiptLongIcon,
    shouldRender: ({ accountantId }) => !!accountantId,
  },
  {
    getName: (t: TFunction) => t('paid-at'),
    extractor: ({ paidAt }) => paidAt,
    Icon: CalendarDaysIcon,
    shouldRender: ({ paidAt }) => !!paidAt,
  },
];

export function InvoiceMainData({ invoice }: InvoiceTabProps): JSX.Element {
  const { t } = useLang();

  return (
    <Column gap="6">
      {!!invoice.cancelationRequestId && (
        <Alert level="warning" title={t('cancelation-requested')} />
      )}

      <Column>
        {invoiceProps.map(
          ({ Icon, getName, extractor, shouldRender }) =>
            (shouldRender?.(invoice) ?? true) && (
              <PropertyWithIcon
                key={`invoice-info-${getName(t)}`}
                icon={<Icon />}
                name={getName(t)}
                value={extractor(invoice, t)}
              />
            ),
        )}
      </Column>

      <h6 className="text-center font-semibold">{t('items-included')}</h6>

      {invoice.entries.map(
        ({ currency, value, project, package: packageData, projectInvoice, type }) => (
          <Row key={projectInvoice ?? packageData?.id} justifyContent="space-between">
            <PropertyWithIcon
              icon={projectInvoice ? <FolderIcon /> : <PackageCheckIcon />}
              containerClassName="phone:flex-col phone:gap-0 phone:items-start"
              name={
                <Row inLine gap="2" className="phone:flex-col phone:items-start phone:gap-0">
                  <Tag name={t(type)} color="primary" round={false} />

                  <TruncateText max={35} text={(project?.name || packageData?.name) as string} />

                  <Row gap="1" className="hidden phone:flex">
                    <span className="text-success-light">{currency}</span>

                    <span>{formatCurrency(value)}</span>
                  </Row>
                </Row>
              }
              alignItems="center"
              value={
                <Row gap="1" className="phone:hidden">
                  <span className="text-success-light">{currency}</span>

                  <span>{formatCurrency(value)}</span>
                </Row>
              }
            />

            {project ? (
              <IconButton
                to="projectProfile"
                params={{ projectId: project.id }}
                linkTarget={project.id}
              >
                <ExternalLinkIcon />
              </IconButton>
            ) : (
              <IconButton
                to="packageProfile"
                params={{ packageId: packageData!.id }}
                linkTarget={packageData!.id}
              >
                <ExternalLinkIcon />
              </IconButton>
            )}
          </Row>
        ),
      )}

      {invoice.clientBalances.map(({ currency, value, type, balanceId }) => (
        <PropertyWithIcon
          key={balanceId}
          icon={<WalletIcon />}
          alignItems="center"
          containerClassName="phone:justify-between"
          name={<Tag name={t('client-balance').toUpperCase()} color="primary" round={false} />}
          value={
            <Row gap="2">
              <Row gap="1">
                <span className="text-success-light">{currency}</span>

                <span>
                  {type === 'OVERPAYMENT' ? '-' : ''} {formatCurrency(value)}
                </span>
              </Row>
            </Row>
          }
        />
      ))}
    </Column>
  );
}
